import React, { useReducer, useState, useEffect, useRef, useContext } from 'react'
import getConfig from 'next/config'
import { setCookie, parseCookies } from 'nookies'
import MarketingHelper from '@zuto-internal/zuto-marketing-helper'
import { GridItem } from '@zuto/core/src/grid'
import { PrivacyNotice, Loading } from '@zuto/molecules'
import $console from '@zuto/utilities/src/$console'
import { useCallbackOnce, useDidMountEffect } from '@zuto/utilities'
import ZutoAnalyticsHelper from '@zuto-internal/zuto-analytics-helper'
import { useDetectAdBlock } from 'adblock-detect-react'
import { ApplicationFormContext, Action, ApplicationFormState } from './ApplicationFormContext'
import mapApplication, { Application } from '../../Mappers/ApplicationFormMapper'
import SubmissionService from '../../services/submission-service'
import EventsService from '../../services/events-service'
import ErrorModal from '../ErrorModal'
import { YEAR } from '../../constants'
import { fireReactGAEvent } from '../Questions/utils'
import { PrefillReviewPageApplicationFormContext } from './PrefillReviewPageApplicationFormContext'
import PrefillReviewPageWrapper from './PrefillReviewPageWrapper'
import MaritalStatusQuestion from '../Questions/MaritalStatusQuestion'
import DateOfBirth from '../Questions/DateOfBirth'
import { GroupedQuestionsForReorderingCase } from '../Questions/GroupedQuestionsForReorderingCase'
import VehicleTypeQuestion from '../Questions/VehicleTypeQuestion'
import DoubleColumnRadioDrivingLicenceQuestion from '../Questions/DrivingLicenceQuestion/DoubleColumnRadioDrivingLicenceQuestion'
import { LoanAmountQuestion } from '../Questions/LoanAmountQuestion'
import { MonthlyIncomeQuestion } from '../Questions/MonthlyIncomeQuestion'
import CardlessEmploymentHistoryQuestion from '../Questions/CardlessEmploymentHistoryQuestion'
import { ApplicationFormProps } from './types'
import { mapAnswers } from '../../utils/ApplicationForm-utils'
import CardlessAddressHistoryQuestion from '../Questions/CardlessAddressHistoryQuestion'
import { AmplitudeEvents, trackAmplitudeEvent } from '../../utils/amplitude-utils'
import Thankyou from '../Thankyou'
import { getNavigationEventLabel } from '../../utils/PrefillReviewPageApplicationForm-utils'
import { zaPrefillAttributesMissing, zaSubmissionError } from '../../utils/monitoring-utils/monitoring-utils'
import { sendFormAbandonmentEvent } from '../../utils/monitoring-utils/sendFormAbandonmentEvent'
import ConflictErrorModalContent from '../ErrorModal/ConflictErrorModalContent'
import { getMissingFields } from '../../utils/monitoring-utils/missing-fields'
import { getSanitisedPrefillData } from '../../utils/prefillData-utils'

export const PrefillReviewPageApplicationForm = ({
  vehicle,
  prefillData,
  lenderDetails,
  eventsServiceUrl,
  customerId,
  experimentName,
  isOfficeTraffic,
  isBotTraffic,
  journey,
  featureSwitches,
  attribution
}: ApplicationFormProps): JSX.Element => {
  const missingFields = getMissingFields(prefillData ?? {})
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()
  const { publicRuntimeConfig } = getConfig()
  const { cookieDomain } = publicRuntimeConfig
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(false)
  const [isDirty, setDirty] = useState(false)
  const [applicationFormCb, resetApplicationFormCb] = useCallbackOnce()
    const [conflictError, setConflictError] = useState(false)
  const { Device: deviceId, Session: sessionId, zuto_wbraid: wbraid, zuto_gbraid: gbraid, zuto_fbclid: fbclid } = parseCookies()
  let adBlockDetected = true
  adBlockDetected = useDetectAdBlock()
  const { showReviewPage, setShowReviewPage, questionName, prevQuestionName, setExperimentName, requiredEntryId, hasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const [sectionStep, setSectionStep] = useState(0)

  const appFormEventParams = {
    Lender: lenderDetails?.lenderName,
    PreApproved: lenderDetails?.preapproved,
    QuoteId: lenderDetails?.quoteId,
    CustomerId: customerId,
    PartnerCustomerId: prefillData?.partnerCustomerId,
    Prefill: !!prefillData,
    DeviceId: deviceId,
    SessionId: sessionId,
    Experiments: experimentName,
    Journey: journey
  }

  const appFormMetadata = {
    deviceId: appFormEventParams.DeviceId,
    sessionId: appFormEventParams.SessionId,
    customerId: appFormEventParams.CustomerId,
    partnerCustomerId: appFormEventParams.PartnerCustomerId,
    lender: appFormEventParams.Lender,
    isPrefill: appFormEventParams.Prefill,
    isPreapproved: appFormEventParams.PreApproved,
    isBotTraffic,
    isOfficeTraffic,
    journey,
    experiments: experimentName,
    wbraid,
    gbraid,
    fbclid
  }

  const eventsService = EventsService(eventsServiceUrl, appFormEventParams)

  useEffect(() => {
    setExperimentName(experimentName)
    zaPrefillAttributesMissing({ missingFields, origin: 'ReviewPage', source: 'PrefillReviewPageApplicationForm'})
  }, [])

  const isRetry = useRef(false)

  const resetUnloadEvents = () => {
    global.onbeforeunload = null
    global.onunload = null
  }

  const submitApplication = (application: Application) => {
    setApiError(false)
    setConflictError(false)
    setLoading(true)
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Submit',
      label: 'FormSubmitted',
      lenderName: prefillData ? lenderDetails?.lenderName || 'zuto' : 'none'
    })
    SubmissionService(publicRuntimeConfig)
      .submitApplication(application)
      .then((response) => {
        // On success, ensure error state is cleared
        setApiError(false)
        setConflictError(false)
        try {
          fireReactGAEvent({
            category: 'Application Form',
            action: 'Submit',
            label: 'Success'
          })
        } catch {
          $console.error('Something went wrong with ReactGA services')
        }
        // fire-and-forget telemetry (don’t block redirect)
        void eventsService.sendAppFormSubmittedEvent(true, isRetry.current).catch((err) => $console.error('sendAppFormStartedEvent failed', err))
        trackAmplitudeEvent(AmplitudeEvents.ApplicationSubmitted, { success: true, is_retry: isRetry.current })
        resetUnloadEvents()
        window.location.assign(response.redirectUrl)
      })
      .catch((err) => {
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Submit',
          label: 'Unsuccessful',
          lenderName: prefillData ? lenderDetails?.lenderName || 'zuto' : 'none'
        })
        // fire-and-forget telemetry
        void eventsService.sendAppFormSubmittedEvent(false, isRetry.current).catch((err) => $console.error('sendAppFormStartedEvent failed', err))
        trackAmplitudeEvent(AmplitudeEvents.ApplicationSubmitted, { success: false, is_retry: isRetry.current })
        isRetry.current = true
        $console.error('Failed to submit application')
        zaSubmissionError(experimentName, err, application.applicantField?.[0])
        if (err && err.response && err.response.status === 409) {
          setConflictError(true)
        } else {
          setApiError(true)
        }
      })
      .finally(() => {
        setLoading(false)
        setCookie(null, 'emailAddress', application.applicantField[0].basicDetailsField.emailField, {
          maxAge: YEAR * 7,
          path: '/',
          domain: cookieDomain
        })
        resetApplicationFormCb()
      })
  }
  const reducer = (state: ApplicationFormState, action: Action): ApplicationFormState => {
    switch (action.type) {
      case 'QUESTION_ANSWERED':
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Next Question',
          label: getNavigationEventLabel(questionName, prevQuestionName, true),
          nonInteraction: false
        })
        return {
          ...state,
          answers: {
            ...state.answers,
            [action.name]: action.value
          },
          lastNavigationAction: 'CONTINUE',
          allErrors: { ...state.allErrors, [action.name]: { ...action.errors } }
        }
      case 'PREVIOUS_QUESTION':
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Previous Question',
          label: getNavigationEventLabel(questionName, prevQuestionName, false),
          nonInteraction: false
        })

        return {
          ...state,
          lastNavigationAction: 'PREVIOUS'
        }
      case 'EDIT_PREFILL_SECTION':
        return {
          ...state,
          prefillSection: action.value
        }
      case 'SAVE':
        return {
          ...state,
          answers: {
            ...state.answers,
            [action.name]: action.value
          },
          allErrors: { ...state.allErrors, [action.name]: { ...action.errors } }
        }
      case 'SUBMIT': {
        const channelAttribution = MarketingHelper.getChannelAttribution()
        const result: Application = mapApplication(
          mapAnswers(state.answers, featureSwitches),
          channelAttribution,
          experimentName,
          action.marketingPreferences,
          {
            ...appFormMetadata,
            isRetry: isRetry.current
          },
          vehicle,
          lenderDetails?.quoteId
        )
        applicationFormCb(() => submitApplication(result))
        return state
      }
      case 'CAN_PROCEED': {
        return {
          ...state,
          canProceed: action.value
        }
      }
      case 'SET_POST_CODE': {
        return {
          ...state,
          postalCode: action.value
        }
      }
      case 'SET_MARKETING_PREFERENCES': {
        return {
          ...state,
          marketingPreferences: {
            ...state.marketingPreferences,
            ...action.value
          }
        }
      }
      default:
        return state
    }
  }

  const initialState: ApplicationFormState = {
    currentQuestionIndex: 0,
    answers: prefillData || {},
    furthestQuestionIndex: 0,
    canProceed: true,
    postalCode: '',
    marketingPreferences: { email: !!featureSwitches.marketingOptInV1, sms: !!featureSwitches.marketingOptInV1 },
    allErrors: {},
    prefillSection: ''
  }
  const [state, dispatch] = useReducer(reducer, initialState)

  const renderIntroductionAndReviewPage = () => (
    <PrefillReviewPageWrapper
      prefillData={state.answers}
      sanitisedPrefillData={getSanitisedPrefillData(state.answers, attribution?.source)}
      isReapply={!!featureSwitches.isReapply}
      isReviewPageV3PreApproval={!!featureSwitches.reviewPageV3PreApproval}
      lenderDetails={lenderDetails}
    />
  )

  const renderMarketingPreferences = () => (
    <Thankyou
      questionKey="thankYou"
      prefillData={state.answers}
      initialAnswerState={initialState.answers}
      attribution={attribution}
    />
  )

  // Used to Navigate to the correct step in each of the sections when 'Required' is clicked.
  useEffect(() => {
    const sectionStepsMap: Record<string, number> = {
      'required-full-name': 0,
      'required-phone-number': 0,
      'required-email-address': 0,
      'required-marital-status': 1,
      'required-dob': 2,
      'required-vehicle-to-finance': 0,
      'required-licence-type': 1,
      'required-loan-amount': 2
    }

    // Address Questions
    if (state.prefillSection === 'edit-address-details') {
      // Check that requiredEntryId starts with required-address- followed by one or more digits
      const requiredAddressDetails = requiredEntryId.match(/^required-address-(\d+)$/)

      if (requiredAddressDetails) {
        setShowReviewPage(false)
        setSectionStep(0)
      }
    }

    // Employment Questions
    if (state.prefillSection === 'edit-employment-details') {
      // Check that requiredEntryId starts with incomplete-address- or required-address- followed by one or more digits
      const incompleteEmploymentEntry = requiredEntryId.match(/^incomplete-employment-(\d+)$/)
      const requiredEmploymentEntry = requiredEntryId.match(/^required-employment-(\d+)$/)
      const requiredMonthlyIncomeEntry = 'required-monthly-income'

      if (requiredMonthlyIncomeEntry) {
        setShowReviewPage(false)
        setSectionStep(0)
      }

      if (incompleteEmploymentEntry || requiredEmploymentEntry) {
        setShowReviewPage(false)
        setSectionStep(1)
      }
    }

    // Other sections (Personal Details and Vehicle Details)
    if (sectionStepsMap[requiredEntryId] !== undefined) {
      setShowReviewPage(false)
      setSectionStep(sectionStepsMap[requiredEntryId])
    }
  }, [requiredEntryId, hasNavigatedFromRequiredClick])

  /**
   * Handles the navigation between steps in a section.
   *
   * @param {boolean} next - Indicates whether the navigation is going to the next step.
   * @param {boolean} lastQuestionInSection - Indicates whether the current step is the last question in the section.
   * @param {boolean} firstQuestionInSection - Indicates whether the current step is the first question in the section.
   * @returns True/False in order to update step count accordingly.
   */
  const stepNavigateHandler = (next: boolean, lastQuestionInSection: boolean, firstQuestionInSection: boolean) => {
    if (lastQuestionInSection || (!next && firstQuestionInSection)) {
      setShowReviewPage(true)
      setSectionStep(0)
    }
    if (next && !lastQuestionInSection) {
      setSectionStep((currStep) => currStep + 1)
    }
    if (!next && !lastQuestionInSection && !firstQuestionInSection) {
      setSectionStep((currStep) => currStep - 1)
    }
  }

  const personalDetailsStepDisplay = (): JSX.Element => {
    if (sectionStep === 1) {
      return (
        <MaritalStatusQuestion
          questionKey="maritalStatus"
          existingAnswers={state.answers.maritalStatus}
          errors={state.allErrors.maritalStatus}
          updateFollowingPrefillStep={stepNavigateHandler}
        />
      )
    }

    if (sectionStep === 2) {
      return (
        <DateOfBirth
          questionKey="dateOfBirth"
          existingAnswers={state.answers.dateOfBirth}
          errors={state.allErrors.dateOfBirth}
          updateFollowingPrefillStep={stepNavigateHandler}
        />
      )
    }

    return (
      <GroupedQuestionsForReorderingCase
        questionKey="personalDetails"
        existingAnswers={state.answers.personalDetails}
        errors={state.allErrors.personalDetails}
        updateFollowingPrefillStep={stepNavigateHandler}
      />
    )
  }

  const vehicleDetailsStepDisplay = (): JSX.Element => {
    if (sectionStep === 1) {
      return (
        <DoubleColumnRadioDrivingLicenceQuestion
          questionKey="drivingLicence"
          existingAnswers={state.answers.drivingLicence}
          errors={state.allErrors.drivingLicence}
          updateFollowingPrefillStep={stepNavigateHandler}
        />
      )
    }

    if (sectionStep === 2) {
      return (
        <LoanAmountQuestion
          questionKey="loanAmount"
          existingAnswers={state.answers.loanAmount}
          errors={state.allErrors.loanAmount}
          updateFollowingPrefillStep={stepNavigateHandler}
        />
      )
    }

    return (
      <VehicleTypeQuestion
        questionKey="vehicleType"
        existingAnswers={state.answers.vehicleType}
        errors={state.allErrors.vehicleType}
        updateFollowingPrefillStep={stepNavigateHandler}
      />
    )
  }

  const addressDetailsStepDisplay = (): JSX.Element => (
    <CardlessAddressHistoryQuestion
      questionKey="addressHistory"
      existingAnswers={state.answers.addressHistory}
      errors={state.allErrors.addressHistory}
      updateFollowingPrefillStep={stepNavigateHandler}
    />
  )

  const employmentDetailsStepDisplay = (): JSX.Element => {
    if (sectionStep === 1) {
      return (
        <CardlessEmploymentHistoryQuestion
          questionKey="employmentHistory"
          existingAnswers={state.answers.employmentHistory}
          errors={state.allErrors.employmentHistory}
          updateFollowingPrefillStep={stepNavigateHandler}
        />
      )
    }

    return (
      <MonthlyIncomeQuestion
        questionKey="monthlyIncome"
        existingAnswers={state.answers.monthlyIncome}
        errors={state.allErrors.monthlyIncome}
        updateFollowingPrefillStep={stepNavigateHandler}
      />
    )
  }

  const renderPrefillSectionQuestions = () => {
    if (state.prefillSection === 'edit-personal-details') return personalDetailsStepDisplay()
    if (state.prefillSection === 'edit-vehicle-details') return vehicleDetailsStepDisplay()
    if (state.prefillSection === 'edit-address-details') return addressDetailsStepDisplay()
    if (state.prefillSection === 'edit-employment-details') return employmentDetailsStepDisplay()

    return null
  }

  const applicationFormIsDirty = () => {
    global.onbeforeunload = () => {
      sendFormAbandonmentEvent({
        eventType: 'beforeUnloadDialogRendered',
        questionName,
        deviceId,
        sessionId,
        experimentName
      })
      return true
    }

    // OnUnload may not be reliable with sending JS events.
    // We need a better way to detect for abandonment.
    global.onpagehide = () => {
      sendFormAbandonmentEvent({
        eventType: 'formAbandonment',
        questionName,
        deviceId,
        sessionId,
        experimentName
      })
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Form Abandonment',
        label: questionName
      })
    }
  }

  useEffect(() => {
    // fire-and-forget telemetry
    void eventsService.sendAppFormStartedEvent().catch((err) => $console.error('sendAppFormStartedEvent failed', err))
    trackAmplitudeEvent(AmplitudeEvents.ApplicationStarted)
    if (adBlockDetected) {
      try {
        zutoAnalyticsHelper.sendCustomEvent({
          eventType: 'TrustPilotDidNotLoad'
        })
      } catch (e) {
        $console.error('zutoAnalyticsHelper.sendCustomEvent error', e)
      }
    }
  }, [eventsServiceUrl])

  useDidMountEffect(() => {
    if (!isDirty) {
      applicationFormIsDirty()
      setDirty(true)
    }
  }, [state.answers])

  useDidMountEffect(() => {
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Form Progression',
      label: questionName
    })
  }, [state.furthestQuestionIndex])

  const closeError = () => {
    setApiError(false)
    setConflictError(false)
    const submitButton = document.querySelector("button[name='submit-button']")
    if (submitButton) (submitButton as HTMLElement).focus()
  }

  return (
    <ApplicationFormContext.Provider
      value={{
        dispatch,
        getAppForm: () => state,
        featureSwitches,
        eventsService
      }}
    >
      <div
        className="application-form-wrapper"
        data-experiment={experimentName}
      >
        {showReviewPage && renderIntroductionAndReviewPage()}
        {showReviewPage && renderMarketingPreferences()}
        {!showReviewPage && renderPrefillSectionQuestions()}
        <GridItem
          gridColumn={['auto / span 12', '3 / span 8']}
          gridRow={['auto', '6 / auto']}
          mt={0}
        >
          <PrivacyNotice />
        </GridItem>
        {loading && <Loading />}
        {apiError && (
          <ErrorModal
            titleText="Sorry"
            ctaText="Please try again"
            onClick={closeError}
          >
            There was a problem submitting your application
          </ErrorModal>
        )}
        {conflictError && (
          <ErrorModal
            ctaText="Close"
            onClick={closeError}
          >
            <ConflictErrorModalContent />
          </ErrorModal>
        )}
      </div>
    </ApplicationFormContext.Provider>
  )
}
